import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditContactSelectField } from '../../../EditContactSelectField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import { EditHrEmployeeSelectField } from '../../../EditHrEmployeeSelectField';
import type { ContactRelationshipInfo } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactRelationshipInfo;
  isDate?: boolean;
}

const RELATIONSHIP_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'relationshipType', formField: 'relationshipType' },
  { apiField: 'metAtThrough', formField: 'metAtOrMetThrough' },
  { apiField: 'placementAgent', formField: 'placementAgent' },
  {
    apiField: 'placementAgentDateAssigned',
    formField: 'placementAgentDateAssigned',
    isDate: true
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactRelationshipInfo,
  initial: ContactRelationshipInfo
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal) !== toDateInputValue(initialVal)
      : currentVal !== initialVal;

    if (hasChanged) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        relationshipType
        ownerId {
          id
          name
        }
        secondaryOwnerId {
          id
          name
        }
        referredBy {
          id
          name
        }
        metAtThrough
        parentId {
          id
          name
        }
        placementAgent
        placementAgentDateAssigned
      }
    }
  }
`;

export interface EditRelationshipInfoDialogProps {
  contactId: string;
  initialValue: ContactRelationshipInfo;
  isOpen: boolean;
  onChange: (data: ContactRelationshipInfo) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactRelationshipInfo;
}

export function EditRelationshipInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditRelationshipInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(
    RELATIONSHIP_INFO_FIELDS,
    value,
    initialValue
  );

  // Handle lookup fields separately
  if (
    value.owner !== initialValue.owner ||
    value.ownerId !== initialValue.ownerId
  ) {
    changes.ownerId = value.ownerId || null;
  }

  if (
    value.secondaryOwner !== initialValue.secondaryOwner ||
    value.secondaryOwnerId !== initialValue.secondaryOwnerId
  ) {
    changes.secondaryOwnerId = value.secondaryOwnerId || null;
  }

  if (
    value.referredBy !== initialValue.referredBy ||
    value.referredById !== initialValue.referredById
  ) {
    changes.referredBy = value.referredById || null;
  }

  if (
    value.parentContact !== initialValue.parentContact ||
    value.parentContactId !== initialValue.parentContactId
  ) {
    changes.parentId = value.parentContactId || null;
  }

  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactRelationshipInfo>(
    field: K,
    fieldValue: ContactRelationshipInfo[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save relationship information');
      } else {
        toast.success('Relationship information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving relationship information:', error);
      toast.error('Failed to save relationship information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Relationship Information"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditDynamicSelectField
          fieldName="relationship_type"
          label="Relationship Type"
          model="res.partner"
          onChange={v => {
            updateField('relationshipType', v);
          }}
          value={value.relationshipType}
        />
        <EditHrEmployeeSelectField
          label="Owner"
          onChange={(id, name) => {
            onChange({
              ...value,
              owner: name,
              ownerId: id
            });
          }}
          value={
            value.ownerId ? { id: value.ownerId, name: value.owner } : null
          }
        />
        <EditHrEmployeeSelectField
          label="Secondary Owner"
          onChange={(id, name) => {
            onChange({
              ...value,
              secondaryOwner: name,
              secondaryOwnerId: id
            });
          }}
          value={
            value.secondaryOwnerId
              ? { id: value.secondaryOwnerId, name: value.secondaryOwner }
              : null
          }
        />
        <EditContactSelectField
          excludeId={contactId}
          label="Referred By"
          onChange={(id, name) => {
            onChange({
              ...value,
              referredBy: name,
              referredById: id
            });
          }}
          value={
            value.referredById
              ? { id: value.referredById, name: value.referredBy }
              : null
          }
        />
        <EditFormField
          label="Met At or Met Through"
          onChange={metAtOrMetThrough => {
            updateField('metAtOrMetThrough', metAtOrMetThrough);
          }}
          value={value.metAtOrMetThrough}
        />
        <EditContactSelectField
          excludeId={contactId}
          label="Parent Contact"
          onChange={(id, name) => {
            onChange({
              ...value,
              parentContact: name,
              parentContactId: id
            });
          }}
          value={
            value.parentContactId
              ? { id: value.parentContactId, name: value.parentContact }
              : null
          }
        />
        <EditDynamicSelectField
          fieldName="placement_agent"
          label="Placement Agent"
          model="res.partner"
          onChange={v => {
            updateField('placementAgent', v);
          }}
          value={value.placementAgent}
        />
        <EditFormField
          label="Placement Agent Date Assigned"
          onChange={placementAgentDateAssigned => {
            updateField(
              'placementAgentDateAssigned',
              placementAgentDateAssigned
            );
          }}
          type="date"
          value={toDateInputValue(value.placementAgentDateAssigned)}
        />
      </div>
    </EditDialog>
  );
}
